import React, { useState, useEffect, useCallback, memo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { tasksApi, projectsApi, teamApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { Plus, GripVertical, Calendar as CalendarIcon, Trash2, Edit, Upload, Link2, X, Download, FileText, Camera, Film, Repeat, Users, Clock } from 'lucide-react';
import { format, startOfDay, isAfter, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useClient } from '../context/ClientContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const API_URL = '';

const PRIORITY_CONFIG = {
  low: { label: 'Baja', color: 'bg-blue-500/20 text-blue-400' },
  medium: { label: 'Media', color: 'bg-yellow-500/20 text-yellow-400' },
  high: { label: 'Alta', color: 'bg-red-500/20 text-red-400' },
};

const STATUS_CONFIG = {
  todo: { label: 'Por hacer', color: 'bg-zinc-500/20 text-zinc-400' },
  in_progress: { label: 'En progreso', color: 'bg-blue-500/20 text-blue-400' },
  review: { label: 'Revision', color: 'bg-orange-500/20 text-orange-400' },
  done: { label: 'Finalizada', color: 'bg-green-500/20 text-green-400' },
};

const COLUMNS = [
  { id: 'todo', title: 'Por Hacer' },
  { id: 'in_progress', title: 'En Progreso' },
  { id: 'review', title: 'En Revision' },
  { id: 'done', title: 'Finalizadas' },
];

// TaskForm extracted OUTSIDE the main component to prevent focus loss
const TaskForm = memo(({ formData, setFormData, onSubmit, submitLabel, isDark, projects, team, datePickerOpen, setDatePickerOpen }) => {

  const toggleAssigned = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...prev.assigned_to, userId]
    }));
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Titulo</Label>
        <Input
          value={formData.title}
          onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''}
          required
          data-testid="task-title-input"
        />
      </div>
      <div>
        <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Descripcion</Label>
        <Textarea
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className={cn('min-h-[80px]', isDark ? 'bg-[#1A1A1A] border-transparent' : '')}
        />
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Prioridad</Label>
          <Select value={formData.priority} onValueChange={v => setFormData(prev => ({ ...prev, priority: v }))}>
            <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent text-white' : 'text-zinc-900'}><SelectValue /></SelectTrigger>
            <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}>
              <SelectItem value="low">Baja</SelectItem><SelectItem value="medium">Media</SelectItem><SelectItem value="high">Alta</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Multi-assign */}
      <div>
        <Label className={cn('flex items-center gap-1', isDark ? 'text-zinc-300' : 'text-zinc-600')}><Users size={14} /> Asignar a (multiple)</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {team.filter(t => t.role !== 'client').map(t => (
            <button key={t.user_id} type="button" onClick={() => toggleAssigned(t.user_id)}
              className={cn('px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                formData.assigned_to.includes(t.user_id)
                  ? 'bg-orange-500/20 text-orange-400 border-orange-500/50'
                  : isDark ? 'border-white/10 text-zinc-400' : 'border-zinc-200 text-zinc-500'
              )} data-testid={`assign-${t.user_id}`}>{t.name}</button>
          ))}
        </div>
      </div>
      {/* Date + Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Fecha limite</Label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn('w-full justify-start text-left font-normal', isDark ? 'bg-[#1A1A1A] border-transparent' : '', !formData.due_date && 'text-zinc-500')}>
                <CalendarIcon className="mr-2 h-4 w-4" />{formData.due_date ? format(new Date(formData.due_date), 'PPP', { locale: es }) : 'Selecciona fecha'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className={cn('w-auto p-0', isDark ? 'bg-[#1A1A1A] border-white/10' : '')}>
              <Calendar mode="single" selected={formData.due_date ? new Date(formData.due_date) : undefined} onSelect={d => { setFormData(prev => ({ ...prev, due_date: d })); setDatePickerOpen(false); }} locale={es} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className={cn('flex items-center gap-1', isDark ? 'text-zinc-300' : 'text-zinc-600')}><Clock size={14} /> Hora limite</Label>
          <Input type="time" value={formData.due_time} onChange={e => setFormData(prev => ({ ...prev, due_time: e.target.value }))} className={isDark ? 'bg-[#1A1A1A] border-transparent text-white' : 'text-zinc-900'} data-testid="task-time-input" />
        </div>
      </div>
      {/* Recurrence */}
      <div>
        <Label className={cn('flex items-center gap-1', isDark ? 'text-zinc-300' : 'text-zinc-600')}><Repeat size={14} /> Repetir</Label>
        <Select value={formData.recurrence || 'none'} onValueChange={v => setFormData(prev => ({ ...prev, recurrence: v }))}>
          <SelectTrigger className={cn('mt-1', isDark ? 'bg-[#1A1A1A] border-transparent text-white' : 'text-zinc-900')}><SelectValue /></SelectTrigger>
          <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}>
            <SelectItem value="none">Sin repeticion</SelectItem>
            <SelectItem value="daily">Diaria</SelectItem>
            <SelectItem value="weekly">Semanal</SelectItem>
            <SelectItem value="monthly">Mensual</SelectItem>
            <SelectItem value="permanent">Permanente</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="task-submit">{submitLabel}</Button>
    </form>
  );
});
TaskForm.displayName = 'TaskForm';

const TasksPage = () => {
  const { selectedClientId } = useClient();
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDark = theme === 'dark';
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [doneFilter, setDoneFilter] = useState('week'); // 'today' | 'week' | 'month' | 'all'
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const emptyForm = { title: '', description: '', project_id: '', assigned_to: [], status: 'todo', priority: 'medium', due_date: null, due_time: '', recurrence: 'none', attachments: [] };
  const [formData, setFormData] = useState(emptyForm);

  const clientId = selectedClientId !== 'all' ? selectedClientId : undefined;

  const fetchData = useCallback(async () => {
    try {
      const [t, p, tm] = await Promise.all([tasksApi.getAll(null, clientId), projectsApi.getAll(clientId), teamApi.getMembers()]);
      setTasks(t.data);
      setProjects(p.data);
      setTeam(tm.data);
    } catch { toast.error('Error al cargar datos'); } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const teamMap = {};
  team.forEach(t => { teamMap[t.user_id] = t.name; });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, project_id: formData.project_id || null, due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null };
      await tasksApi.create(data);
      toast.success('Tarea creada');
      setCreateOpen(false);
      setFormData(emptyForm);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    try {
      const data = { ...formData, project_id: formData.project_id || null, due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null };
      await tasksApi.update(selectedTask.task_id, data);
      toast.success('Tarea actualizada');
      setEditOpen(false);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleDelete = async (taskId) => {
    try {
      await tasksApi.delete(taskId);
      toast.success('Tarea eliminada');
      setDetailOpen(false);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleStatusChange = async (taskId, status) => {
    try {
      await tasksApi.updateStatus(taskId, status);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (selectedTask) {
      setUploading(true);
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          const res = await tasksApi.uploadAttachment(selectedTask.task_id, fd);
          setSelectedTask(prev => ({ ...prev, attachments: [...(prev.attachments || []), res.data] }));
        } catch { toast.error('Error al subir archivo'); }
      }
      setUploading(false);
      fetchData();
    }
  };

  const openEdit = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title, description: task.description || '', project_id: task.project_id || '',
      assigned_to: Array.isArray(task.assigned_to) ? task.assigned_to : task.assigned_to ? [task.assigned_to] : [],
      status: task.status, priority: task.priority,
      due_date: task.due_date ? new Date(task.due_date) : null,
      due_time: task.due_time || '',
      recurrence: task.recurrence || 'none',
      attachments: task.attachments || []
    });
    setEditOpen(true);
  };

  const openDetail = (task) => { setSelectedTask(task); setDetailOpen(true); };

  const handleDragStart = (e, taskId) => { e.dataTransfer.setData('taskId', taskId); };
  const handleDrop = (e, status) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId) handleStatusChange(taskId, status);
  };

  const RECURRENCE_LABELS = { daily: 'Diaria', weekly: 'Semanal', monthly: 'Mensual', permanent: 'Permanente' };

  if (loading) return <DashboardLayout title="Tareas"><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout title="Tareas">
      <div className="space-y-6" data-testid="tasks-page">
        <div className="flex justify-between items-center">
          <h2 className={cn('text-xl font-semibold', isDark ? 'text-white' : 'text-zinc-900')}>Tablero de Tareas</h2>
          <Dialog open={createOpen} onOpenChange={o => { setCreateOpen(o); if (!o) setFormData(emptyForm); }}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="create-task-btn"><Plus size={16} className="mr-1" /> Nueva Tarea</Button>
            </DialogTrigger>
            <DialogContent className={cn('max-w-lg max-h-[85vh] overflow-y-auto', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
              <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Crear Tarea</DialogTitle></DialogHeader>
              <TaskForm formData={formData} setFormData={setFormData} onSubmit={handleCreate} submitLabel="Crear Tarea" isDark={isDark} projects={projects} team={team} datePickerOpen={datePickerOpen} setDatePickerOpen={setDatePickerOpen} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Kanban Board */}
        <div className="grid grid-cols-4 gap-4">
          {COLUMNS.map(col => {
            let colTasks = tasks.filter(t => t.status === col.id);
            // For "done" column, filter out old finished tasks to avoid clutter
            if (col.id === 'done' && doneFilter !== 'all') {
              const cutoff = doneFilter === 'today' ? subDays(new Date(), 1)
                : doneFilter === 'week' ? subDays(new Date(), 7)
                : subDays(new Date(), 30);
              colTasks = colTasks.filter(t => {
                const ref = t.updated_at || t.created_at;
                if (!ref) return false;
                return isAfter(new Date(ref), startOfDay(cutoff));
              });
            }
            return (
              <div key={col.id} className={cn('rounded-xl p-3 min-h-[400px]', isDark ? 'bg-[#0D0D0D]' : 'bg-zinc-100')}
                onDragOver={e => e.preventDefault()} onDrop={e => handleDrop(e, col.id)}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={cn('text-sm font-semibold', isDark ? 'text-zinc-300' : 'text-zinc-700')}>{col.title}</h3>
                  <span className={cn('text-xs px-2 py-0.5 rounded-full', isDark ? 'bg-white/5 text-zinc-400' : 'bg-zinc-200 text-zinc-500')}>{colTasks.length}</span>
                </div>
                {col.id === 'done' && (
                  <div className={cn('mb-3 inline-flex rounded-lg border p-0.5 text-[10px]', isDark ? 'border-white/10 bg-[#141414]' : 'border-zinc-200 bg-white')}>
                    {[
                      { v: 'today', l: 'Hoy' },
                      { v: 'week', l: '7d' },
                      { v: 'month', l: '30d' },
                      { v: 'all', l: 'Todas' },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        onClick={() => setDoneFilter(opt.v)}
                        className={cn('px-2 py-0.5 rounded transition-colors', doneFilter === opt.v ? 'bg-orange-500 text-white' : isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-700')}
                        data-testid={`done-filter-${opt.v}`}
                      >{opt.l}</button>
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  {colTasks.map(task => {
                    const pConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                    const assigned = Array.isArray(task.assigned_to) ? task.assigned_to : task.assigned_to ? [task.assigned_to] : [];
                    const recLabel = RECURRENCE_LABELS[task.recurrence];
                    return (
                      <div key={task.task_id} draggable onDragStart={e => handleDragStart(e, task.task_id)}
                        className={cn('p-3 rounded-lg border cursor-grab active:cursor-grabbing transition-all hover:shadow-md group', isDark ? 'bg-[#141414] border-white/5 hover:border-orange-500/30' : 'bg-white border-zinc-200 hover:border-orange-400/40')}
                        onClick={() => openDetail(task)} data-testid={`task-card-${task.task_id}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <GripVertical size={14} className="text-zinc-500" />
                          <div className="flex items-center gap-1">
                            {recLabel && (
                              <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400 font-medium" data-testid={`recurrence-badge-${task.task_id}`}>
                                <Repeat size={9} /> {recLabel}
                              </span>
                            )}
                            <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', pConfig.color)}>{pConfig.label}</span>
                          </div>
                        </div>
                        <h4 className={cn('text-sm font-medium mb-1', isDark ? 'text-white' : 'text-zinc-900')}>{task.title}</h4>
                        {task.due_date && <p className="text-[10px] text-zinc-500">{format(new Date(task.due_date), 'd MMM', { locale: es })}{task.due_time ? ` ${task.due_time}` : ''}</p>}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex -space-x-1.5">
                            {assigned.slice(0, 3).map(uid => (
                              <div key={uid} className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center ring-1 ring-zinc-800" title={teamMap[uid]}>
                                <span className="text-orange-400 text-[8px] font-bold">{(teamMap[uid] || '?')[0]}</span>
                              </div>
                            ))}
                            {assigned.length > 3 && <span className="text-[9px] text-zinc-500 ml-1">+{assigned.length - 3}</span>}
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={e => { e.stopPropagation(); openEdit(task); }} className={isDark ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-zinc-700'} data-testid={`edit-task-${task.task_id}`}><Edit size={12} /></button>
                            <button onClick={e => { e.stopPropagation(); handleDelete(task.task_id); }} className="text-zinc-500 hover:text-red-400" data-testid={`delete-task-${task.task_id}`}><Trash2 size={12} /></button>
                          </div>
                        </div>
                        {(task.attachments?.length > 0) && <div className="flex items-center gap-1 mt-1.5"><FileText size={10} className="text-zinc-500" /><span className="text-[9px] text-zinc-500">{task.attachments.length} archivos</span></div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className={cn('max-w-lg max-h-[85vh] overflow-y-auto', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
            <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Editar Tarea</DialogTitle></DialogHeader>
            <TaskForm formData={formData} setFormData={setFormData} onSubmit={handleEdit} submitLabel="Guardar Cambios" isDark={isDark} projects={projects} team={team} datePickerOpen={datePickerOpen} setDatePickerOpen={setDatePickerOpen} />
            {/* File upload for editing */}
            {selectedTask && (
              <div className="mt-4">
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Archivos adjuntos</Label>
                <label className={cn('flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed cursor-pointer mt-1', isDark ? 'border-white/10 hover:border-orange-500/40' : 'border-zinc-300 hover:border-orange-500/40')}>
                  <Upload size={16} className="text-zinc-400" />
                  <span className="text-sm text-zinc-400">{uploading ? 'Subiendo...' : 'Subir archivos'}</span>
                  <input type="file" multiple onChange={handleFileUpload} className="hidden" data-testid="task-file-upload" />
                </label>
                {(selectedTask.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedTask.attachments.map((a, i) => {
                      const src = a.url?.startsWith('http') ? a.url : `${API_URL}${a.url}`;
                      const downloadUrl = `${API_URL}/api/download/${(a.url || '').split('/').pop()}`;
                      return (
                        <div key={i} className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg border', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}>
                          {a.file_type === 'image' ? <Camera size={12} className="text-green-400" /> : a.file_type === 'video' ? <Film size={12} className="text-blue-400" /> : <FileText size={12} className="text-orange-400" />}
                          <span className={cn('text-xs max-w-[100px] truncate', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{a.name}</span>
                          <a href={downloadUrl} download className="text-zinc-400 hover:text-orange-400" data-testid={`download-attach-${i}`}><Download size={12} /></a>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className={cn('max-w-lg max-h-[85vh] overflow-y-auto', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
            {selectedTask && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>{selectedTask.title}</DialogTitle>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setDetailOpen(false); openEdit(selectedTask); }} className={isDark ? 'border-white/10 text-zinc-300' : ''} data-testid="edit-task-detail"><Edit size={14} className="mr-1" /> Editar</Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(selectedTask.task_id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-task-detail"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex gap-2 flex-wrap">
                    <span className={cn('px-2 py-1 rounded text-xs', STATUS_CONFIG[selectedTask.status]?.color)}>{STATUS_CONFIG[selectedTask.status]?.label}</span>
                    <span className={cn('px-2 py-1 rounded text-xs', PRIORITY_CONFIG[selectedTask.priority]?.color)}>{PRIORITY_CONFIG[selectedTask.priority]?.label}</span>
                    {selectedTask.recurrence && selectedTask.recurrence !== 'none' && (
                      <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400">
                        <Repeat size={10} /> {RECURRENCE_LABELS[selectedTask.recurrence] || selectedTask.recurrence}
                      </span>
                    )}
                  </div>
                  {selectedTask.description && <p className={cn('text-sm', isDark ? 'text-zinc-300' : 'text-zinc-700')}>{selectedTask.description}</p>}
                  {selectedTask.due_date && (
                    <p className={cn('text-sm', isDark ? 'text-zinc-400' : 'text-zinc-500')}>Fecha: {format(new Date(selectedTask.due_date), 'PPP', { locale: es })}{selectedTask.due_time ? ` a las ${selectedTask.due_time}` : ''}</p>
                  )}
                  <div>
                    <Label className="text-zinc-500 text-xs">Asignados</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(Array.isArray(selectedTask.assigned_to) ? selectedTask.assigned_to : selectedTask.assigned_to ? [selectedTask.assigned_to] : []).map(uid => (
                        <span key={uid} className={cn('px-2 py-1 rounded text-xs', isDark ? 'bg-orange-500/10 text-orange-400' : 'bg-orange-100 text-orange-600')}>{teamMap[uid] || uid}</span>
                      ))}
                    </div>
                  </div>
                  {/* Attachments */}
                  {(selectedTask.attachments || []).length > 0 && (
                    <div>
                      <Label className="text-zinc-500 text-xs mb-2 block">Archivos ({selectedTask.attachments.length})</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {selectedTask.attachments.map((a, i) => {
                          const src = a.url?.startsWith('http') ? a.url : `${API_URL}${a.url}`;
                          const downloadUrl = `${API_URL}/api/download/${(a.url || '').split('/').pop()}`;
                          const isImg = a.file_type === 'image';
                          return (
                            <div key={i} className={cn('rounded-lg overflow-hidden border', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}>
                              {isImg ? <img src={src} alt={a.name} className="w-full h-20 object-cover" /> : (
                                <div className="w-full h-20 flex items-center justify-center">{a.file_type === 'video' ? <Film size={20} className="text-blue-400" /> : <FileText size={20} className="text-orange-400" />}</div>
                              )}
                              <div className="flex items-center justify-between px-2 py-1">
                                <span className={cn('text-[10px] truncate flex-1', isDark ? 'text-zinc-400' : 'text-zinc-500')}>{a.name}</span>
                                <a href={downloadUrl} download className="text-zinc-400 hover:text-orange-400 shrink-0" data-testid={`download-detail-${i}`}><Download size={12} /></a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Upload in detail view */}
                  <label className={cn('flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed cursor-pointer', isDark ? 'border-white/10 hover:border-orange-500/40' : 'border-zinc-300 hover:border-orange-500/40')}>
                    <Upload size={16} className="text-zinc-400" />
                    <span className="text-sm text-zinc-400">{uploading ? 'Subiendo...' : 'Subir archivos'}</span>
                    <input type="file" multiple onChange={handleFileUpload} className="hidden" />
                  </label>
                  {/* Status change */}
                  <div>
                    <Label className="text-zinc-500 text-xs mb-2 block">Cambiar estado</Label>
                    <div className="flex gap-2 flex-wrap">
                      {COLUMNS.map(col => (
                        <Button key={col.id} size="sm" variant={selectedTask.status === col.id ? 'default' : 'outline'}
                          onClick={() => { handleStatusChange(selectedTask.task_id, col.id); setSelectedTask({ ...selectedTask, status: col.id }); }}
                          className={selectedTask.status === col.id ? 'bg-orange-500' : isDark ? 'border-white/10 text-zinc-300' : ''}
                          data-testid={`status-${col.id}`}>{col.title}</Button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TasksPage;
